import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { estadoDeFuente, type EstadoConexionFuente } from "../datos/estadoFuente";
import type { FuentesConectadas } from "../datos/fuentesConectadas";
import { MarcoLogo, type MarcaLogo } from "./LogosMarcas";
import { AccionesFuente, PildoraEstado } from "./piezasConexion";

/**
 * Las tres fuentes de datos y su estado REAL de conexión.
 *
 * Los cuatro estados de una fuente con integración —cargando, conectado, sin
 * conectar, falló la consulta— se dibujan DISTINTO. En particular "falló" no
 * comparte nada con "sin conectar": si se pintaran igual, un error de red le
 * diría al usuario que su Instagram está desconectado (y lo mandaría a
 * reconectar algo que ya estaba andando). Ver `datos/estadoFuente.ts`.
 *
 * Cada acción va a un lugar que EXISTE:
 *  · Instagram  → Configuración con la sección `instagram` abierta
 *                 (`Settings.tsx` la levanta de `location.state.section`).
 *  · Tiendanube → Mi marca, con el deep-link `?conectar=tiendanube` que ya usa
 *                 el banner de la home para abrir el modal de OAuth.
 *  · Meta Ads   → NADA. No hay integración, así que la tarjeta va apagada y
 *                 rotulada "Próximamente", sin botón: un botón que no hace nada
 *                 es peor que no tenerlo.
 */

const RUTA_INSTAGRAM = "/configuracion";
/** La sección que abre `Settings.tsx` para `InstagramIntegration`. */
const SECCION_INSTAGRAM = "instagram";
const RUTA_TIENDANUBE = "/mi-marca";
const RUTA_TIENDANUBE_CONECTAR = "/mi-marca?conectar=tiendanube";

/* La píldora de estado y los botones (`PildoraEstado`, `AccionesFuente`) viven
   en `piezasConexion.tsx`: los comparte el paso de conexiones de la Puesta en
   marcha, que contesta la misma pregunta sobre la misma cuenta y no puede
   contestarla distinto. El anillo de foco y las clases de los botones se
   fueron con ellos. */

interface PropsTarjeta {
  marca: MarcaLogo;
  nombre: string;
  descripcion: string;
  estado: EstadoConexionFuente;
  /** Qué se ve debajo de la píldora en cada estado que lo necesita. */
  detalle: string;
  acciones: ReactNode;
}

const Tarjeta = ({ marca, nombre, descripcion, estado, detalle, acciones }: PropsTarjeta) => (
  // Radios concéntricos: la tarjeta es 16 (rounded-2xl) y adentro sólo hay
  // círculos y píldoras, que no tienen esquina que empatar.
  <div
    className={`flex flex-col rounded-2xl border border-[#02062B]/[0.08] p-5 ${
      estado === "no-disponible" ? "bg-[#FAFBFD]" : "bg-white"
    }`}
  >
    <div className="flex items-center gap-3">
      <MarcoLogo marca={marca} apagado={estado === "no-disponible"} />
      <div className="min-w-0">
        <p className="truncate text-[14px] font-semibold text-brand-black">{nombre}</p>
        <p className="truncate text-[12px] text-[#02062B]/[0.5]">{descripcion}</p>
      </div>
    </div>

    {/* `aria-live`: el estado cambia solo cuando vuelve la consulta, sin que el
        usuario toque nada. Sin esto, un lector de pantalla se queda con el
        "Consultando…" del primer render.

        LA REGIÓN ENVUELVE LAS TRES COSAS QUE CAMBIAN JUNTAS —píldora, detalle y
        botones—, no sólo la píldora. Cuando vuelve la consulta caída, la píldora
        dice el diagnóstico ("No pudimos consultarlo") pero la mitad accionable
        está afuera: la instrucción vive en el detalle ("probá de nuevo") y el
        primario pasa a decir "Reintentar", con un segundo botón que se monta.
        Con la región sobre la píldora sola, el anuncio automático llegaba
        mutilado justo en el estado de error que esta pantalla existe para
        comunicar bien. Agrandarla no agrega verbosidad: el contenido inicial de
        una live region no se anuncia, sólo los cambios posteriores. */}
    <div aria-live="polite">
      <div className="mt-4">
        <PildoraEstado estado={estado} />
      </div>

      <p className="mt-2 text-[12.5px] leading-relaxed text-[#02062B]/[0.55]">{detalle}</p>

      <div className="mt-4 flex flex-wrap items-center gap-1 pt-1">{acciones}</div>
    </div>
  </div>
);

export const SeccionConexiones = ({
  fuentes,
  cargandoPerfil,
  perfilFallo,
  onReintentar,
}: {
  fuentes: FuentesConectadas;
  /**
   * El perfil/las marcas todavía se están resolviendo. Va aparte de
   * `fuentes.cargando` porque el hook recibe `idCompany` en `null` mientras
   * carga el perfil y respondería "sin conectar" en un abrir y cerrar de ojos
   * — un parpadeo que dice justo lo contrario de lo que después se muestra.
   */
  cargandoPerfil: boolean;
  /**
   * El perfil terminó de cargar y quedó en `null`: la consulta a `profiles`
   * falló. Sin esto, la tarjeta de Instagram decía "Sin conectar" en un caso en
   * el que NUNCA SE PREGUNTÓ NADA: sin `id_company`, `useFuentesConectadas` ni
   * llega a tocar `instagram_data`, así que no hay `error` de Supabase que
   * levantar y el booleano queda en `false` por omisión. Es el cuarto camino
   * que faltaba, el mismo que `MarcaPage` ya trataba aparte (`perfilFallo`).
   */
  perfilFallo: boolean;
  /**
   * Reintenta TODO lo que pudo fallar: el perfil (que es de quien depende el
   * `id_company`) y las dos consultas de fuentes. Lo arma la pantalla porque el
   * perfil no es de este componente.
   */
  onReintentar: () => void;
}) => {
  const navigate = useNavigate();
  const cargando = cargandoPerfil || fuentes.cargando;

  /**
   * `reintentando` distingue "cargando porque recién abrí la pantalla" de
   * "cargando porque apreté Reintentar": sólo en el segundo caso hay un foco
   * que cuidar y un rótulo que no puede volver a decir "Conectar X". Se apaga
   * solo cuando la consulta termina.
   */
  const [reintentando, setReintentando] = useState(false);
  useEffect(() => {
    if (!cargando) setReintentando(false);
  }, [cargando]);
  const pedirReintento = () => {
    setReintentando(true);
    onReintentar();
  };

  // Si el que falló fue el perfil, NINGUNA de las dos consultas vale: sin
  // `id_company` no se preguntó por Instagram, y sin marca activa el estado de
  // Tiendanube es el de "ninguna marca", que no es el de la marca del usuario.
  const estadoInstagram = estadoDeFuente({
    cargando,
    conectado: fuentes.instagram,
    fallo: fuentes.falloInstagram || perfilFallo,
  });
  const estadoTiendanube = estadoDeFuente({
    cargando,
    conectado: fuentes.tiendanube,
    fallo: fuentes.falloTiendanube || perfilFallo,
  });

  const irAInstagram = () =>
    navigate(RUTA_INSTAGRAM, { state: { section: SECCION_INSTAGRAM } });

  const detalleInstagram: Record<EstadoConexionFuente, string> = {
    cargando: "Estamos consultando el estado de tu cuenta.",
    // Lo que Atlas hace HOY con esta conexión es publicar y programar; de las
    // métricas orgánicas todavía no lee nada (no hay un solo consumidor de
    // insights en el repo). Prometer una lectura que no ocurre, en la pantalla
    // que existe para decir la verdad sobre el estado real, es el peor lugar
    // posible para hacerlo.
    conectado: "Tu cuenta está vinculada: Atlas puede publicar y programar en tu feed.",
    "sin-conectar": "Conectala para que Atlas pueda publicar y programar en tu feed.",
    error:
      "No pudimos consultar el estado de esta conexión. Puede ser un problema de red — probá de nuevo.",
    "no-disponible": "",
  };
  const detalleTiendanube: Record<EstadoConexionFuente, string> = {
    cargando: "Estamos consultando el estado de tu tienda.",
    conectado: "Atlas está leyendo tu catálogo y tus productos.",
    "sin-conectar": "Conectala para que Atlas conozca tu catálogo y tus ventas.",
    error:
      "No pudimos consultar el estado de esta conexión. Puede ser un problema de red — probá de nuevo.",
    "no-disponible": "",
  };

  return (
    <section>
      {/* Sin bajada: Matías pidió sacar los subtítulos de esta pantalla. La que
          había acá aclaraba que el estado de estas tarjetas es el REAL de la
          cuenta, a diferencia del resto del bloque, que muestra ejemplos. Esa
          distinción ahora la carga cada tarjeta con su propio estado. */}
      <h2 className="text-[16px] font-semibold text-brand-black">Conexiones</h2>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Tarjeta
          marca="instagram"
          nombre="Instagram"
          descripcion="Tu cuenta y lo que publicás"
          estado={estadoInstagram}
          detalle={detalleInstagram[estadoInstagram]}
          acciones={
            <AccionesFuente
              estado={estadoInstagram}
              reintentando={reintentando}
              onReintentar={pedirReintento}
              textoConectar="Conectar Instagram"
              onConectar={irAInstagram}
              textoIr="Ir a la configuración"
              onIr={irAInstagram}
            />
          }
        />

        <Tarjeta
          marca="meta"
          nombre="Meta Ads"
          descripcion="Campañas y pauta paga"
          estado="no-disponible"
          detalle="Todavía no existe la integración con Meta Ads: Atlas no lee nada de tu pauta. Cuando esté, se conecta desde acá."
          acciones={null}
        />

        <Tarjeta
          marca="tiendanube"
          nombre="Tiendanube"
          descripcion="Catálogo y productos"
          estado={estadoTiendanube}
          detalle={detalleTiendanube[estadoTiendanube]}
          acciones={
            <AccionesFuente
              estado={estadoTiendanube}
              reintentando={reintentando}
              onReintentar={pedirReintento}
              textoConectar="Conectar Tiendanube"
              onConectar={() => navigate(RUTA_TIENDANUBE_CONECTAR)}
              textoIr="Ir a Mi marca"
              onIr={() => navigate(RUTA_TIENDANUBE)}
            />
          }
        />
      </div>
    </section>
  );
};

export default SeccionConexiones;
